import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Divider,
  Skeleton,
  SkeletonText,
  Tag,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
} from "@chakra-ui/react";
import { PlusIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { CopyText } from "../../components/Utilities/CopyText";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { EMLConfigurationDetailForm } from "../../forms/EMLConfigurationDetailForm";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { cloneDeep } from "lodash";

interface EMLConfigurationDetailProps {
  create?: boolean;
}

export const EMLConfigurationDetail = (props: EMLConfigurationDetailProps) => {
  const [searchParams] = useSearchParams();
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [emlConfiguration, setEmlConfiguration] =
    useState<CherryHubAdminApi.EMLConfiguration | null>(null);
  const [terminalUrls, setTerminalUrls] = useState<{ [key: string]: string }>(
    {}
  );

  const {
    data: emlConfigurationDoc,
    isLoading,
    error,
  } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId) {
        return null;
      }
      return apiClient.getEMLConfiguration(businessId);
    },
    [businessId]
  );
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );

  useEffect(() => {
    if (emlConfigurationDoc != null && props.create) {
      setEditMode(false);
      navigate(`/admin/businesses/${businessId}/eml-configuration`);
    }
    setEmlConfiguration(emlConfigurationDoc);
  }, [emlConfigurationDoc]);

  const onSuccess = useCallback(
    (emlConfiguration: CherryHubAdminApi.EMLConfiguration) => {
      setEmlConfiguration(emlConfiguration);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("EML Configuration created.");
        navigate(`/admin/businesses/${businessId}/eml-configuration`);
      } else {
        showSuccessToast("EML Configuration updated.");
      }
    },
    [showSuccessToast, emlConfiguration]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "EML Configuration update failed.");
    },
    [showErrorToast]
  );

  useEffect(() => {
    emlConfiguration?.SelfServeMemberTerminals?.forEach((terminal) => {
      apiClient
        .getCherryPaySelfServeMemberTerminalUrl(
          businessId!,
          terminal.TerminalId,
          terminal.CheckCode
        )
        .then((result) => {
          if (result.ok) {
            let newValue = cloneDeep(terminalUrls);
            newValue[terminal.CheckCode] = result.data.Value as string;
            setTerminalUrls(newValue);
          }
        });
    });
  }, [emlConfiguration]);

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} EML Configuration
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/eml-configuration`}
              >
                EML Configuration
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {(isLoading || !emlConfiguration) && !props.create && !error && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <HStack w="100%" justifyContent="space-between">
                <Skeleton w="320px" height="40px" />
                <Skeleton w="80px" height="40px" />
              </HStack>
              <SkeletonText w="50%" mt="4" noOfLines={7} spacing="4" />
              <SkeletonText w="100%" mt="4" noOfLines={4} spacing="4" />
            </VStack>
          )}

          {!isLoading && error && !editMode && (
            <VStack w="100%" p={4} spacing={2}>
              <Text fontStyle="italic" fontSize="sm">
                EML Configuration has not been set.
              </Text>
              <Button
                size="sm"
                leftIcon={<PlusIcon />}
                onClick={() => {
                  navigate(
                    `/admin/businesses/${businessId}/eml-configuration/new`
                  );
                  setEditMode(true);
                }}
                colorScheme="cherryButton"
              >
                Create EML Configuration
              </Button>
            </VStack>
          )}

          {!isLoading && editMode && (!props.create ? emlConfiguration : true) && (
            <EMLConfigurationDetailForm
              apiClient={apiClient}
              businessId={businessId!}
              emlConfiguration={emlConfiguration}
              onCancel={() => {
                if (props.create)
                  navigate(`/admin/businesses/${businessId}/eml-configuration`);
                setEditMode(false);
              }}
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          )}

          {!isLoading && !editMode && emlConfiguration && (
            <>
              <HStack w="100%">
                <Box flexGrow={1} pb={4}>
                  <CopyText
                    text={emlConfiguration?.Meta?.Id!}
                    hover
                    adjustMargin
                  >
                    <Text fontSize="md" color="cherryUi.600">
                      {emlConfiguration?.Meta?.Id}
                    </Text>
                  </CopyText>
                  {/* <Text fontSize="xl" color="cherryUi.600">
                    <b>{emlConfiguration?.BusinessId}</b>
                  </Text> */}
                </Box>

                <Box>
                  <BusinessMenu
                    businessId={businessId!}
                    onClick={() => setEditMode(true)}
                  />
                </Box>
              </HStack>

              <ViewControl
                label="Default Disbursement Account Id"
                value={emlConfiguration?.DefaultDisbursementAccountId ?? "-"}
              />

              <ViewControl
                label="Default Plastic Gift Card Program"
                value={emlConfiguration?.DefaultPlasticGiftCardProgram ?? "-"}
              />

              <ViewControl
                label="Default Instant Gift Card Program"
                value={emlConfiguration?.DefaultInstantGiftCardProgram ?? "-"}
              />

              <ViewControl
                label="Default Reloadable Card Program"
                value={emlConfiguration?.DefaultReloadableCardProgram ?? "-"}
              />

              <ViewControl
                label="Default Salary Packaging Program"
                value={emlConfiguration?.DefaultSalaryPackagingProgram ?? "-"}
                pb={4}
              />

              <Accordion
                w="100%"
                allowToggle
                allowMultiple
                variant="section"
                defaultIndex={[0, 1, 2, 3]}
              >
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Format</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="Default Reloadable Card Program Invitation SMS Message Body Format"
                            value={
                              emlConfiguration?.DefaultReloadableCardProgramInvitationSMSMessageBodyFormat
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Default Instant Gift Invitation SMS Message Body Format"
                            value={
                              emlConfiguration?.DefaultInstantGiftInvitationSMSMessageBodyFormat
                            }
                          />
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Programs</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <Accordion allowMultiple variant="detail-section">
                          {(emlConfiguration.Programs &&
                            Object.keys(emlConfiguration.Programs).length &&
                            Object.entries(emlConfiguration.Programs).map(
                              ([key, program]) => (
                                <AccordionItem key={key}>
                                  <h2>
                                    <AccordionButton>
                                      <Box flex="1" textAlign="left">
                                        <b>{program.Description}&nbsp;</b>
                                        <span
                                          style={{
                                            fontSize: "10px",
                                            fontWeight: "normal",
                                            color:
                                              "var(--chakra-colors-gray-300)",
                                          }}
                                        >
                                          (#{key})
                                        </span>
                                      </Box>
                                      <Box flexShrink={2}>
                                        <Tag
                                          size="sm"
                                          bgColor={
                                            program.IsEnabled
                                              ? ""
                                              : "cherryButton.500"
                                          }
                                          color={
                                            program.IsEnabled ? "" : "white"
                                          }
                                        >
                                          {program.IsEnabled
                                            ? "Enabled"
                                            : "Disabled"}
                                        </Tag>
                                      </Box>
                                      <AccordionIcon />
                                    </AccordionButton>
                                  </h2>
                                  <AccordionPanel width="100%">
                                    <VStack w="100%" alignItems="flex-start">
                                      <ViewControl
                                        label="Program Id"
                                        value={program?.ProgramId ?? "-"}
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Company Id"
                                        value={program?.CompanyId ?? "-"}
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Program Type"
                                        value={program?.ProgramType ?? "-"}
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Delivery Types"
                                        value={
                                          program?.DeliveryTypes?.join(", ") ??
                                          "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="MDES Config Id"
                                        value={program?.MDESConfigId ?? "-"}
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Disbursement Account Id"
                                        value={
                                          program?.DisbursementAccountId ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Minimum Initial Card Value"
                                        value={
                                          program?.MinimumInitialCardValue?.toFixed(
                                            1
                                          ) ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Maximum Initial Card Value"
                                        value={
                                          program?.MaximumInitialCardValue?.toFixed(
                                            1
                                          ) ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Minimum Add Funds To Card Value"
                                        value={
                                          program?.MinimumAddFundsToCardValue?.toFixed(
                                            1
                                          ) ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Maximum Add Funds To Card Value"
                                        value={
                                          program?.MaximumAddFundsToCardValue?.toFixed(
                                            1
                                          ) ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Minimum Points Transfer To Card Value"
                                        value={
                                          program?.MinimumPointsTransferToCardValue?.toFixed(
                                            1
                                          ) ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Maximum Points Transfer To Card Value"
                                        value={
                                          program?.MaximumPointsTransferToCardValue?.toFixed(
                                            1
                                          ) ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Lock Reloadable Card To Company"
                                        value={
                                          program?.LockReloadableCardToCompany
                                            ? "Yes"
                                            : "No"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Hide All Cards In Cherry Pay App"
                                        value={
                                          program?.HideAllCardsInCherryPayApp
                                            ? "Yes"
                                            : "No"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Default Offer Expiry Days"
                                        value={
                                          program?.DefaultOfferExpiryDays ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Default Plastic Expiry Months"
                                        value={
                                          program?.DefaultPlasticExpiryMonths ??
                                          "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Card Order OTP Validation Attempt Limit"
                                        value={
                                          program?.CardOrderOTPValidationAttemptLimit ??
                                          "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl label="Fee Configuration">
                                        <Box flex={3}>
                                          {(program?.FeeConfiguration && (
                                            <Table size="sm">
                                              <Thead>
                                                <Tr>
                                                  <Th>Configuration</Th>
                                                  <Th>Value</Th>
                                                </Tr>
                                              </Thead>
                                              <Tbody>
                                                <Tr>
                                                  <Td>
                                                    Funds Load Fee Payable By
                                                    Party
                                                  </Td>
                                                  <Td>
                                                    {
                                                      program?.FeeConfiguration
                                                        ?.FundsLoadFeePayableByParty
                                                    }
                                                  </Td>
                                                </Tr>

                                                <Tr>
                                                  <Td>
                                                    Funds Load Fee Percent
                                                  </Td>
                                                  <Td>
                                                    {
                                                      program?.FeeConfiguration
                                                        ?.FundsLoadFeePercent
                                                    }
                                                  </Td>
                                                </Tr>

                                                <Tr>
                                                  <Td>
                                                    Loyalty Points Transfer To
                                                    Card Fee Payable By Party
                                                  </Td>
                                                  <Td>
                                                    {
                                                      program?.FeeConfiguration
                                                        ?.LoyaltyPointsTransferToCardFeePayableByParty
                                                    }
                                                  </Td>
                                                </Tr>

                                                <Tr>
                                                  <Td>
                                                    Loyalty Points Transfer To
                                                    Card Fee Percent
                                                  </Td>
                                                  <Td>
                                                    {
                                                      program?.FeeConfiguration
                                                        ?.LoyaltyPointsTransferToCardFeePercent
                                                    }
                                                  </Td>
                                                </Tr>
                                              </Tbody>
                                            </Table>
                                          )) || (
                                            <Text fontStyle="italic">
                                              Fee Configuration has not been
                                              set.
                                            </Text>
                                          )}
                                        </Box>
                                      </ViewControl>

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl label="Card Activation Configuration">
                                        <Box flex={3}>
                                          {(program?.CardActivationConfiguration && (
                                            <Table size="sm">
                                              <Thead>
                                                <Tr>
                                                  <Th>Configuration</Th>
                                                  <Th>Value</Th>
                                                </Tr>
                                              </Thead>
                                              <Tbody>
                                                <Tr>
                                                  <Td>Card Program Name</Td>
                                                  <Td>
                                                    {
                                                      program
                                                        ?.CardActivationConfiguration
                                                        ?.CardProgramName
                                                    }
                                                  </Td>
                                                </Tr>

                                                <Tr>
                                                  <Td>
                                                    Card Activation Theme Data
                                                  </Td>
                                                  <Td>
                                                    <pre
                                                      style={{
                                                        fontSize:
                                                          "var(--chakra-fontSizes-xs)",
                                                      }}
                                                    >
                                                      {program
                                                        ?.CardActivationConfiguration
                                                        ?.CardActivationThemeData !=
                                                        null &&
                                                        JSON.stringify(
                                                          program
                                                            ?.CardActivationConfiguration
                                                            ?.CardActivationThemeData,
                                                          null,
                                                          2
                                                        )}
                                                    </pre>
                                                  </Td>
                                                </Tr>
                                              </Tbody>
                                            </Table>
                                          )) || (
                                            <Text fontStyle="italic">
                                              Card Activation Configuration has
                                              not been set.
                                            </Text>
                                          )}
                                        </Box>
                                      </ViewControl>
                                      <ViewControl label="Card Transfer Limits Configuration">
                                        <Box flex={3}>
                                          {(program?.CardProgramTransferLimit && (
                                            <Table size="sm">
                                              <Thead>
                                                <Tr>
                                                  <Th>Configuration</Th>
                                                  <Th>Value</Th>
                                                </Tr>
                                              </Thead>
                                              <Tbody>
                                                <Tr>
                                                  <Td>
                                                    Is Limit Check Enabled
                                                  </Td>
                                                  <Td>
                                                    {program
                                                      ?.CardProgramTransferLimit
                                                      ?.IsLimitCheckedOnTransfer
                                                      ? "Yes"
                                                      : "No"}
                                                  </Td>
                                                </Tr>

                                                <Tr>
                                                  <Td>Time Period Value</Td>
                                                  <Td>
                                                    {
                                                      program
                                                        ?.CardProgramTransferLimit
                                                        ?.TimePeriod?.Quantity
                                                    }
                                                  </Td>
                                                </Tr>
                                                <Tr>
                                                  <Td>Time Period Type</Td>
                                                  <Td>
                                                    {
                                                      program
                                                        ?.CardProgramTransferLimit
                                                        ?.TimePeriod?.Period
                                                    }
                                                  </Td>
                                                </Tr>
                                                <Tr>
                                                  <Td>Dollar Limit</Td>
                                                  <Td>
                                                    {
                                                      program
                                                        ?.CardProgramTransferLimit
                                                        ?.DollarLimit
                                                    }
                                                  </Td>
                                                </Tr>
                                              </Tbody>
                                            </Table>
                                          )) || (
                                            <Text fontStyle="italic">
                                              Card Activation Configuration has
                                              not been set.
                                            </Text>
                                          )}
                                        </Box>
                                      </ViewControl>
                                    </VStack>
                                  </AccordionPanel>
                                </AccordionItem>
                              )
                            )) || (
                            <Text fontStyle="italic">
                              Programs has not been set.
                            </Text>
                          )}
                        </Accordion>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Self Serve Member Terminals</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <Accordion allowMultiple variant="detail-section">
                          {(emlConfiguration.SelfServeMemberTerminals &&
                            emlConfiguration.SelfServeMemberTerminals.length &&
                            emlConfiguration.SelfServeMemberTerminals.map(
                              (config, configIdx) => (
                                <AccordionItem key={configIdx}>
                                  <h2>
                                    <AccordionButton gap={2}>
                                      <Box flex="1" textAlign="left">
                                        <b>
                                          #{config.TerminalId}&nbsp;
                                          <span
                                            style={{
                                              fontSize: "10px",
                                              fontWeight: "normal",
                                              color:
                                                "var(--chakra-colors-gray-300)",
                                            }}
                                          >
                                            ({config.CheckCode})
                                          </span>
                                        </b>
                                      </Box>
                                      {terminalUrls && (
                                        <>
                                          <Box>
                                            <PreviewButton
                                              url={
                                                terminalUrls[
                                                  config.CheckCode
                                                ] as string
                                              }
                                              fontSize="xs"
                                            >
                                              Open
                                            </PreviewButton>
                                          </Box>
                                          <Box>
                                            <CopyText
                                              text={
                                                terminalUrls[
                                                  config.CheckCode
                                                ] as string
                                              }
                                              fontSize="xs"
                                            >
                                              Copy
                                            </CopyText>
                                          </Box>
                                        </>
                                      )}
                                      <Box flexShrink={2}>
                                        <Tag
                                          size="sm"
                                          bgColor={
                                            config.IsEnabled
                                              ? ""
                                              : "cherryButton.500"
                                          }
                                          color={
                                            config.IsEnabled ? "" : "white"
                                          }
                                        >
                                          {config.IsEnabled
                                            ? "Enabled"
                                            : "Disabled"}
                                        </Tag>
                                      </Box>
                                      <AccordionIcon />
                                    </AccordionButton>
                                  </h2>
                                  <AccordionPanel width="100%">
                                    <VStack w="100%" alignItems="flex-start">
                                      <ViewControl
                                        label="Check Code"
                                        value={config?.CheckCode ?? "-"}
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Allowed IP Addresses"
                                        value={
                                          config?.AllowedIPAddresses?.join(
                                            ", "
                                          ) ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Member Lookup Method"
                                        value={
                                          config?.MemberLookupMethod ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Allowable Input String Characters"
                                        value={
                                          config?.AllowableInputStringCharacters ??
                                          "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Member Sign In Prompt"
                                        value={
                                          config?.MemberSignInPrompt ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Logo Url"
                                        value={config?.LogoUrl ?? "-"}
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl
                                        label="Member Filter Querystring"
                                        value={
                                          config?.MemberFilterQuerystring ?? "-"
                                        }
                                      />

                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl label="Theme Data">
                                        <Box
                                          flex={3}
                                          width="100%"
                                          borderWidth="1px"
                                          borderRadius="lg"
                                          boxShadow="lg"
                                          overflowX="auto"
                                          padding={6}
                                          position="relative"
                                          minHeight="90px"
                                        >
                                          <pre
                                            style={{
                                              fontSize:
                                                "var(--chakra-fontSizes-xs)",
                                            }}
                                          >
                                            {config?.ThemeData != null &&
                                              JSON.stringify(
                                                config?.ThemeData,
                                                null,
                                                2
                                              )}
                                          </pre>
                                        </Box>
                                      </ViewControl>
                                    </VStack>
                                  </AccordionPanel>
                                </AccordionItem>
                              )
                            )) || (
                            <Text fontStyle="italic">
                              Member Terminal has not been set.
                            </Text>
                          )}
                        </Accordion>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Member Filtering</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="Restrict CherryPay App Members"
                            value={
                              emlConfiguration?.MemberFilterConfiguration
                                ?.RestrictCherryPayAppMembers
                                ? "Yes"
                                : "No"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Use Business Digital Membership Active Member Filter"
                            value={
                              emlConfiguration?.MemberFilterConfiguration
                                ?.UseBusinessDigitalMembershipActiveMemberFilter
                                ? "Yes"
                                : "No"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Member Filter Querystring"
                            value={
                              emlConfiguration?.MemberFilterConfiguration
                                ?.MemberFilterQuerystring ?? "-"
                            }
                          />
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              </Accordion>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
